import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import { selectCurrentUser } from 'store/user/selectors';
import { selectCancellationCandidates } from 'store/subscriptions/selectors';

import { DISCOUNT_TYPES } from 'constants/subscriptions';
import { CANCEL_SUBSCRIPTION_SCREEN_ID } from 'constants/analytics';

import { trackScreenLoad } from 'services/Analytics/trackers/mainTrackers';
import { sendAnalyticCancelOfferClick } from 'services/Analytics';

import useModals from 'modals/hooks';
import { useSelector } from 'hooks';
import { useGetTexts } from '../../hooks/useGetTexts';
import useCancelSubscriptionFlow from '../../../hooks/useCancelSubscriptionFlow';
import { checkDiscountAllowed } from '../../../hooks/helpers';

import { getValuesByKey } from 'helpers/utils';
import { isMainSubscription } from 'helpers/subscriptions';

import OutlinedButton from 'components/Buttons/OutlinedButton';
import { CustomPrimaryButton } from 'components/Buttons';
import BaseConfirmationModal from './components/BaseConfirmationModal';

import { CancelOfferAction } from 'types/subscription';

const ConfirmationModalWithDiscountControl = () => {
    const { t } = useTranslation();

    const { closeModal } = useModals();

    const { country } = useSelector(selectCurrentUser);

    const { onCancelSubscriptionWithOffer, onCancelSubscription } = useCancelSubscriptionFlow();

    const cancellationCandidates = useSelector(selectCancellationCandidates);
    const subscription_id = getValuesByKey(cancellationCandidates, 'external_id');

    const isMultiSubs = cancellationCandidates && cancellationCandidates?.length > 1;

    const { title, subtitle } = useGetTexts({
        titleText: isMultiSubs
            ? 'subscription.confirmationModalWithDiscount.title.multi'
            : 'subscription.confirmationModalWithDiscount.title.single',
        subtitleText: isMultiSubs
            ? 'subscription.confirmationModalWithDiscount.subtitle.multi'
            : 'subscription.confirmationModalWithDiscount.subtitle.single',
    });

    useEffect(() => {
        if (subscription_id) {
            trackScreenLoad(CANCEL_SUBSCRIPTION_SCREEN_ID, {
                subscription_id,
            });
        }
    }, [subscription_id]);

    const onSubmit = () => {
        sendAnalyticCancelOfferClick({
            screenId: CANCEL_SUBSCRIPTION_SCREEN_ID,
            actionEvent: CancelOfferAction.Cancel,
            eventLabel: { subscription_id },
        });

        if (cancellationCandidates?.[0]) {
            const {
                product_type,
                discount: { discount_type },
            } = cancellationCandidates[0];

            isMainSubscription(product_type) && discount_type !== DISCOUNT_TYPES.extra && checkDiscountAllowed(country)
                ? onCancelSubscriptionWithOffer()
                : onCancelSubscription();
        }
    };

    const onBackButtonClick = () => {
        sendAnalyticCancelOfferClick({
            screenId: CANCEL_SUBSCRIPTION_SCREEN_ID,
            actionEvent: CancelOfferAction.Close,
            eventLabel: { subscription_id },
        });

        closeModal();
    };

    return (
        <BaseConfirmationModal
            title={title}
            subtitle={subtitle}
            onBackButtonClick={onBackButtonClick}
            dataTestId="confirmationModalWithDiscountControl"
        >
            <CustomPrimaryButton dataLocator="cancelBtn" onClick={onSubmit} mb={8}>
                {t('basics.button.cancelSubscription')}
            </CustomPrimaryButton>
            <OutlinedButton
                dataLocator="goBackBtn"
                onClick={onBackButtonClick}
                text={t('basics.button.goBack')}
                backgroundColor="surface-main"
                mb={4}
            />
        </BaseConfirmationModal>
    );
};

export default ConfirmationModalWithDiscountControl;
